import { createContext, useContext, useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import styles from './Dialog.module.css';

/**
 * Window chrome (the desktop client's title bar) every dialog renders pinned to the top of the
 * viewport. A modal dialog makes the rest of the page inert, so the app's own title bar would stop
 * working while one is open.
 */
export const DialogChromeContext = createContext<ReactNode>(null);

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Overrides the chrome from `DialogChromeContext` for this dialog. */
  chrome?: ReactNode;
  /**
   * `wide` fits a table, `medium` a form with long values (paths, version names); the default
   * suits short forms and lists.
   */
  size?: 'default' | 'medium' | 'wide';
  /**
   * Keeps the title and footer in place and scrolls only the body when the content is taller than
   * the viewport. The body never scrolls sideways.
   */
  scrollBody?: boolean;
};

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  chrome,
  size = 'default',
  scrollBody = false,
}: DialogProps) {
  const contextChrome = useContext(DialogChromeContext);
  const shownChrome = chrome ?? contextChrome;
  const hasChrome = shownChrome !== null && shownChrome !== undefined && shownChrome !== false;
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    if (open && !el.open) {
      opener.current = document.activeElement;
      // jsdom lacks showModal; fall back to the open attribute there.
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
      restoreFocus();
    }

    function restoreFocus() {
      const target = opener.current;
      opener.current = null;
      if (target instanceof HTMLElement && target.isConnected) target.focus();
    }
  }, [open]);

  useEffect(
    () => () => {
      const target = opener.current;
      if (target instanceof HTMLElement && target.isConnected) target.focus();
    },
    [],
  );

  return (
    <dialog
      ref={ref}
      className={[
        styles['dialog'],
        size === 'default' ? '' : styles[size],
        scrollBody ? styles['scrollBody'] : '',
        hasChrome ? styles['withChrome'] : '',
      ]
        .filter((name) => name !== '')
        .join(' ')}
      aria-labelledby={titleId}
      // React hands a nested dialog's cancel and clicks to the enclosing dialogs too, so Escape
      // or a backdrop click would close all of them; only this one closes.
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        event.stopPropagation();
        onClose();
      }}
    >
      <div className={styles['content']}>
        <h2 id={titleId} className={styles['title']}>
          {title}
        </h2>
        <div className={styles['body']}>{children}</div>
        {footer !== undefined && <div className={styles['footer']}>{footer}</div>}
      </div>
      {/* After the content, so showModal() focuses the dialog's own controls first. */}
      {hasChrome && <div className={styles['chrome']}>{shownChrome}</div>}
    </dialog>
  );
}
